import { useState, useEffect } from 'react';
import { useOrg } from '../contexts/OrgContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import {
  exportConfiguration,
  exportFlocs,
  exportAssets,
  exportSchedules,
  parseImportFile,
  executeImport,
  ImportResult,
  ImportExecutionResult,
} from '../services/bulkOperationsService';
import { getFlocs } from '../services/flocService';
import { generateFlocQRCode, printBulkQRCodeLabels } from '../services/qrCodeService';
import { FunctionalLocation } from '@easydone/shared';
import { FlocTreeSelect } from '../components/FlocTreeSelect';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline';

export function BulkOperationsPage() {
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const canManage = usePermission('inspections.manage');

  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [executionResult, setExecutionResult] = useState<ImportExecutionResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showResult, setShowResult] = useState(false);

  // QR Code bulk export
  const [flocs, setFlocs] = useState<FunctionalLocation[]>([]);
  const [selectedFlocId, setSelectedFlocId] = useState<string>('');
  const [showQRTreeSelector, setShowQRTreeSelector] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);

  useEffect(() => {
    loadFlocs();
  }, [currentOrg]);

  const loadFlocs = async () => {
    if (!currentOrg) return;
    try {
      const data = await getFlocs(currentOrg.id);
      setFlocs(data);
    } catch (error) {
      console.error('Failed to load FLOCs:', error);
    }
  };

  const handleExportAll = async () => {
    if (!currentOrg) return;

    setExporting(true);
    try {
      await exportConfiguration(currentOrg.id);
      alert('Configuration exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export configuration');
    } finally {
      setExporting(false);
    }
  };

  const handleExportFlocs = async () => {
    if (!currentOrg) return;

    setExporting(true);
    try {
      await exportFlocs(currentOrg.id);
      alert('FLOCs exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export FLOCs');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAssets = async () => {
    if (!currentOrg) return;

    setExporting(true);
    try {
      await exportAssets(currentOrg.id);
      alert('Assets exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export assets');
    } finally {
      setExporting(false);
    }
  };

  const handleExportSchedules = async () => {
    if (!currentOrg) return;

    setExporting(true);
    try {
      await exportSchedules(currentOrg.id);
      alert('Schedules exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export schedules');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const result = await parseImportFile(file);
      setImportResult(result);
      setShowPreview(true);
    } catch (error) {
      console.error('Import failed:', error);
      alert('Failed to parse import file');
    } finally {
      setImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importResult || !currentOrg || !currentUser) return;

    setImporting(true);
    try {
      const result = await executeImport(currentOrg.id, importResult.data, currentUser.uid);
      setExecutionResult(result);
      setShowPreview(false);
      setShowResult(true);
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setImporting(false);
    }
  };

  const handleBulkQRExport = async () => {
    if (!currentOrg) return;

    setGeneratingQR(true);
    try {
      // Get FLOCs to generate QR codes for
      let flocsToExport: FunctionalLocation[] = [];

      if (selectedFlocId) {
        // Get selected FLOC and all its descendants
        const selectedFloc = flocs.find(f => f.id === selectedFlocId);
        if (selectedFloc) {
          flocsToExport = [selectedFloc];
          // Add all descendants (FLOCs whose path includes the selected FLOC ID)
          flocsToExport.push(...flocs.filter(f => f.path.includes(selectedFlocId)));
        }
      } else {
        // Export all FLOCs
        flocsToExport = flocs;
      }

      if (flocsToExport.length === 0) {
        alert('No FLOCs to export');
        return;
      }

      // Generate QR codes for all selected FLOCs
      const qrCodes: Array<{ url: string; title: string; subtitle: string }> = [];

      for (const floc of flocsToExport) {
        try {
          const url = await generateFlocQRCode(floc.id);
          qrCodes.push({
            url,
            title: floc.code,
            subtitle: floc.name,
          });
        } catch (error) {
          console.error(`Failed to generate QR for ${floc.code}:`, error);
        }
      }

      if (qrCodes.length === 0) {
        alert('Failed to generate any QR codes');
        return;
      }

      // Print bulk QR codes
      printBulkQRCodeLabels(qrCodes);
      setShowQRTreeSelector(false);
    } catch (error) {
      console.error('Bulk QR export failed:', error);
      alert('Failed to generate bulk QR codes');
    } finally {
      setGeneratingQR(false);
    }
  };

  if (!canManage) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">You don't have permission to access bulk operations</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Bulk Operations</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          Export and import configuration data using Excel files
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Bulk QR Code Export Section */}
        <div className="card">
          <div className="flex items-center mb-4">
            <QrCodeIcon className="w-6 h-6 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Bulk QR Code Export</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Generate and print QR code labels for FLOCs. Select a parent location to export all its children, or export all FLOCs.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => setShowQRTreeSelector(true)}
              disabled={generatingQR}
              className="btn btn-primary w-full flex items-center justify-center"
            >
              <QrCodeIcon className="w-5 h-5 mr-2" />
              {generatingQR ? 'Generating...' : 'Generate QR Codes'}
            </button>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-xs text-blue-800">
              <strong>Tip:</strong> QR codes will be printed in a 3-column grid layout, perfect for label sheets. Each QR code includes the FLOC code and name.
            </p>
          </div>
        </div>

        {/* Export Section */}
        <div className="card">
          <div className="flex items-center mb-4">
            <ArrowDownTrayIcon className="w-6 h-6 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Export Configuration</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Download your current configuration data as an Excel file. You can modify the data and
            re-import it later.
          </p>

          <div className="space-y-3">
            <button
              onClick={handleExportAll}
              disabled={exporting}
              className="btn btn-primary w-full flex items-center justify-center"
            >
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              {exporting ? 'Exporting...' : 'Export All Configuration'}
            </button>

            <div className="border-t border-gray-200 pt-3">
              <p className="text-xs text-gray-500 mb-2">Export individual entities:</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleExportFlocs}
                  disabled={exporting}
                  className="btn btn-secondary text-sm"
                >
                  FLOCs
                </button>
                <button
                  onClick={handleExportAssets}
                  disabled={exporting}
                  className="btn btn-secondary text-sm"
                >
                  Assets
                </button>
                <button
                  disabled={exporting}
                  className="btn btn-secondary text-sm opacity-50 cursor-not-allowed"
                  title="Coming soon"
                >
                  Templates
                </button>
                <button
                  onClick={handleExportSchedules}
                  disabled={exporting}
                  className="btn btn-secondary text-sm"
                >
                  Schedules
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-xs text-blue-800">
              <strong>Tip:</strong> Exported files include instructions on how to modify and
              re-import the data.
            </p>
          </div>
        </div>

        {/* Import Section */}
        <div className="card">
          <div className="flex items-center mb-4">
            <ArrowUpTrayIcon className="w-6 h-6 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Import Configuration</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Upload an Excel file to bulk create or update FLOCs, assets, templates, and schedules.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Excel File
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={importing}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary-50 file:text-primary-700
                  hover:file:bg-primary-100
                  cursor-pointer"
              />
            </div>

            {importing && (
              <div className="flex items-center justify-center py-4">
                <div className="text-gray-500">Parsing file...</div>
              </div>
            )}
          </div>

          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-xs text-yellow-800">
              <strong>Warning:</strong> Import will create new records or update existing ones.
              Always review the preview before confirming.
            </p>
          </div>
        </div>
      </div>

      {/* Import Preview Modal */}
      {showPreview && importResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Import Preview</h2>

            {importResult.success ? (
              <>
                <div className="flex items-center text-green-600 mb-4">
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  <span className="font-medium">File parsed successfully</span>
                </div>

                <div className="space-y-3 mb-6">
                  {importResult.data.flocs && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="font-medium text-blue-900">
                        FLOCs: {importResult.data.flocs.length} record(s)
                      </p>
                    </div>
                  )}
                  {importResult.data.assets && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded">
                      <p className="font-medium text-green-900">
                        Assets: {importResult.data.assets.length} record(s)
                      </p>
                    </div>
                  )}
                  {importResult.data.templates && (
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                      <p className="font-medium text-purple-900">
                        Templates: {importResult.data.templates.length} record(s)
                      </p>
                    </div>
                  )}
                  {importResult.data.schedules && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                      <p className="font-medium text-orange-900">
                        Schedules: {importResult.data.schedules.length} record(s)
                      </p>
                    </div>
                  )}
                </div>

                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded mb-6">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> This will create new records or update existing ones
                    in your organization. Please review carefully before proceeding.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center text-red-600 mb-4">
                  <XCircleIcon className="w-5 h-5 mr-2" />
                  <span className="font-medium">Errors found</span>
                </div>

                <div className="space-y-2 mb-6">
                  {importResult.errors.map((error, index) => (
                    <div key={index} className="p-3 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowPreview(false);
                  setImportResult(null);
                }}
                className="btn btn-secondary w-full sm:w-auto"
              >
                Cancel
              </button>
              {importResult.success && (
                <button
                  onClick={handleConfirmImport}
                  disabled={importing}
                  className="btn btn-primary w-full sm:w-auto"
                >
                  {importing ? 'Importing...' : 'Confirm Import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Results Modal */}
      {showResult && executionResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Import Complete</h2>

            {executionResult.success ? (
              <>
                <div className="flex items-center text-green-600 mb-4">
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  <span className="font-medium">Import successful!</span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="p-3 bg-green-50 border border-green-200 rounded">
                    <p className="font-medium text-green-900">FLOCs</p>
                    <p className="text-sm text-green-700">
                      Created: {executionResult.created.flocs}, Updated: {executionResult.updated.flocs}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="font-medium text-blue-900">Assets</p>
                    <p className="text-sm text-blue-700">
                      Created: {executionResult.created.assets}, Updated: {executionResult.updated.assets}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                    <p className="font-medium text-purple-900">Schedules</p>
                    <p className="text-sm text-purple-700">
                      Created: {executionResult.created.schedules}, Updated: {executionResult.updated.schedules}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center text-orange-600 mb-4">
                  <XCircleIcon className="w-5 h-5 mr-2" />
                  <span className="font-medium">Import completed with errors</span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="p-3 bg-green-50 border border-green-200 rounded">
                    <p className="font-medium text-green-900">Successfully Imported</p>
                    <p className="text-sm text-green-700">
                      FLOCs: {executionResult.created.flocs} created, {executionResult.updated.flocs} updated
                    </p>
                    <p className="text-sm text-green-700">
                      Assets: {executionResult.created.assets} created, {executionResult.updated.assets} updated
                    </p>
                    <p className="text-sm text-green-700">
                      Schedules: {executionResult.created.schedules} created, {executionResult.updated.schedules} updated
                    </p>
                  </div>

                  {executionResult.errors.length > 0 && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                      <p className="font-medium text-red-900 mb-2">
                        Errors ({executionResult.errors.length})
                      </p>
                      <div className="max-h-60 overflow-y-auto space-y-1">
                        {executionResult.errors.map((error, index) => (
                          <p key={index} className="text-sm text-red-800">
                            {error}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowResult(false);
                  setExecutionResult(null);
                  setImportResult(null);
                }}
                className="btn btn-primary w-full sm:w-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Tree Selector Modal */}
      {showQRTreeSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Select FLOC Tree Level</h2>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Select a parent FLOC to generate QR codes for it and all its children, or leave unselected to export all FLOCs.
              </p>

              {flocs.length > 0 ? (
                <div className="border border-gray-300 rounded-lg p-3 max-h-96 overflow-y-auto">
                  <FlocTreeSelect
                    flocs={flocs}
                    selectedId={selectedFlocId}
                    onSelect={setSelectedFlocId}
                  />
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No FLOCs available
                </div>
              )}

              {selectedFlocId && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-800">
                    <strong>Selected:</strong> {flocs.find(f => f.id === selectedFlocId)?.name}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    Will export {1 + flocs.filter(f => f.path.includes(selectedFlocId)).length} FLOC(s)
                  </p>
                </div>
              )}

              {!selectedFlocId && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    No selection - will export all {flocs.length} FLOC(s)
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowQRTreeSelector(false);
                  setSelectedFlocId('');
                }}
                className="btn btn-secondary w-full sm:w-auto"
                disabled={generatingQR}
              >
                Cancel
              </button>
              <button
                onClick={() => setSelectedFlocId('')}
                className="btn btn-secondary w-full sm:w-auto"
                disabled={generatingQR}
              >
                Clear Selection
              </button>
              <button
                onClick={handleBulkQRExport}
                disabled={generatingQR || flocs.length === 0}
                className="btn btn-primary w-full sm:flex-1"
              >
                {generatingQR ? 'Generating...' : 'Generate & Print'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Documentation Section */}
      <div className="mt-6 card bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">How It Works</h3>
        <div className="space-y-3 text-sm text-gray-700">
          <div>
            <strong>1. Export:</strong> Download your current configuration as an Excel file with
            multiple sheets (FLOCs, Assets, Templates, Schedules).
          </div>
          <div>
            <strong>2. Modify:</strong> Edit the Excel file to add new records or update existing
            ones. Don't modify IDs for existing records.
          </div>
          <div>
            <strong>3. Import:</strong> Upload the modified file. Review the preview to see what
            will be created or updated.
          </div>
          <div>
            <strong>4. Confirm:</strong> Confirm the import to apply the changes to your
            organization.
          </div>
        </div>
      </div>
    </div>
  );
}
